// ============================================================================
// Reminders API
// ============================================================================
// CRUD operations for scheduled reminders with multi-recipient support
// ============================================================================

import { supabase, auth } from 'app/auth/auth.js';
import { profileCacheService } from 'shared/services/profile-cache-service.js';

/**
 * Create a reminder with recipients
 * @param {Object} params
 * @param {string} params.title - Reminder note/description
 * @param {string} params.remindAt - ISO timestamp with offset (e.g. '2026-06-25T14:00:00+02:00')
 * @param {string} [params.contextType] - 'task', 'service', 'dashboard', 'general'
 * @param {string} [params.contextId] - Related entity ID
 * @param {string} [params.contextName] - Display name for context
 * @param {string[]} params.recipientIds - User IDs to notify (should include current user)
 * @returns {Promise<{data: Object|null, error: Error|null}>}
 */
export async function createReminder({ title, remindAt, contextType, contextId, contextName, recipientIds }) {
    const userId = auth.user?.id;
    if (!userId) return { data: null, error: new Error('No user') };

    // Ensure creator is always a recipient
    const allRecipients = new Set(recipientIds || []);
    allRecipients.add(userId);

    // Insert reminder
    const { data: reminder, error: reminderError } = await supabase
        .from('reminders')
        .insert({
            created_by: userId,
            title,
            remind_at: remindAt,
            context_type: contextType || null,
            context_id: contextId || null,
            context_name: contextName || null
        })
        .select()
        .single();

    if (reminderError) {
        console.error('[RemindersAPI] Error creating reminder:', reminderError);
        return { data: null, error: reminderError };
    }

    // Insert recipients
    const recipientRows = [...allRecipients].map(uid => ({
        reminder_id: reminder.id,
        user_id: uid
    }));

    const { error: recipientsError } = await supabase
        .from('reminder_recipients')
        .insert(recipientRows);

    if (recipientsError) {
        console.error('[RemindersAPI] Error adding recipients:', recipientsError);
    }

    return { data: reminder, error: null };
}

/**
 * Get reminders for the current user (as creator or recipient)
 * RLS ensures only visible reminders are returned.
 * @param {Object} [options]
 * @param {boolean} [options.upcoming=true] - Only show reminders from the start of today onward
 * @returns {Promise<{data: Array, error: Error|null}>}
 */
export async function getMyReminders({ upcoming = true } = {}) {
    const userId = auth.user?.id;
    if (!userId) return { data: [], error: null };

    // Fetch reminders (RLS filters to user's own + recipient)
    let query = supabase
        .from('reminders')
        .select(`
            id,
            created_by,
            title,
            remind_at,
            context_type,
            context_id,
            context_name,
            is_sent,
            created_at,
            recipients:reminder_recipients (
                user_id
            )
        `)
        .order('remind_at', { ascending: true });

    if (upcoming) {
        const startOfToday = new Date();
        startOfToday.setHours(0, 0, 0, 0);
        query = query.gte('remind_at', startOfToday.toISOString());
    }

    const { data, error } = await query;

    if (error) {
        console.error('[RemindersAPI] Error fetching reminders:', error);
        return { data: [], error };
    }

    if (!data || data.length === 0) return { data: [], error: null };

    // Resolve creator profiles via ProfileCacheService
    const creatorIds = [...new Set(data.map(r => r.created_by))];
    const profileMap = await profileCacheService.getProfiles(creatorIds);

    // Attach creator profile to each reminder
    const enriched = data.map(r => ({
        ...r,
        creator: profileMap.get(r.created_by) || null
    }));

    return { data: enriched, error: null };
}

/**
 * Delete a reminder (only creator can delete via RLS)
 * @param {string} reminderId
 * @returns {Promise<{success: boolean, error: Error|null}>}
 */
export async function deleteReminder(reminderId) {
    const { error } = await supabase
        .from('reminders')
        .delete()
        .eq('id', reminderId);

    if (error) {
        console.error('[RemindersAPI] Error deleting reminder:', error);
        return { success: false, error };
    }

    return { success: true, error: null };
}

/**
 * Get count of upcoming unsent reminders for current user
 * @returns {Promise<number>}
 */
export async function getRemindersCount() {
    const userId = auth.user?.id;
    if (!userId) return 0;

    const startOfToday = new Date();
    startOfToday.setHours(0, 0, 0, 0);

    // Count reminders visible to user that are upcoming and unsent
    const { count, error } = await supabase
        .from('reminders')
        .select('id', { count: 'exact', head: true })
        .gte('remind_at', startOfToday.toISOString())
        .eq('is_sent', false);

    if (error) {
        console.error('[RemindersAPI] Error getting count:', error);
        return 0;
    }

    return count || 0;
}
